import { useCallback, useMemo, useState } from 'react'
import Fuse from 'fuse.js'
import { useDebounceValue } from 'usehooks-ts'
import { useRouterState } from '@tanstack/react-router'
import { countByType, groupResults, rankSearch } from './rankSearch'
import type { SearchFilter, SearchItem, SearchScope } from './rankSearch'
import { useTasks } from '@/lib/db/rxdb/hooks/entities/tasks'
import { useProjects } from '@/lib/db/rxdb/hooks/entities/projects'
import { usePages } from '@/lib/db/rxdb/hooks/entities/pages'
import { usePersonalNotes } from '@/lib/db/rxdb/hooks/entities/personal-notes'
import { useStatuses } from '@/lib/db/rxdb/hooks/entities/statuses'
import { resolveTaskIdentifier } from '@/routes/features/tasks/utils/taskIdentifier'
import useLoginStore from '@/store/LoginStore'

export type { SearchFilter, SearchItem, SearchItemType, SearchScope } from './rankSearch'

const RECENT_ITEMS_KEY = (workspaceId: string) => `moly-recent-search-${workspaceId}`
const MAX_RECENT_ITEMS = 10
/** Per group in "All" (design 29c: "capped at 4 each with N more"). */
const ALL_GROUP_CAP = 4
const FILTERED_CAP = 50

function getRecentItems(workspaceId: string): Array<SearchItem> {
  try {
    const stored = localStorage.getItem(RECENT_ITEMS_KEY(workspaceId))
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function saveRecentItems(workspaceId: string, items: Array<SearchItem>) {
  try {
    localStorage.setItem(RECENT_ITEMS_KEY(workspaceId), JSON.stringify(items))
  } catch {
    // localStorage quota exceeded or unavailable
  }
}

export interface ProjectInfo {
  id: string
  projectKey: string
  name: string
  parentProjectId: string | null
  icon: string | null
  iconType: string | null
  iconColor: string | null
  isCyclesEnabled?: boolean
}

export function useSearch() {
  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebounceValue(query, 120)
  const [activeFilter, setActiveFilter] = useState<SearchFilter>('all')
  /** undefined = follow the page you're on; null = every space. */
  const [scopeOverride, setScopeOverride] = useState<SearchScope | null | undefined>(undefined)

  const workspaceId = useLoginStore((s) => s.workspaceId)
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  const { tasks, isLoading: tasksLoading } = useTasks({ isDeleted: false })
  const { projects, isLoading: projectsLoading } = useProjects()
  const { pages, isLoading: pagesLoading } = usePages()
  const { notes, isLoading: notesLoading } = usePersonalNotes()
  const { statuses } = useStatuses()

  const isLoading = tasksLoading || projectsLoading || pagesLoading || notesLoading

  const projectMap = useMemo(() => {
    const map = new Map<string, ProjectInfo>()
    for (const p of projects) {
      if (p.isDeleted) continue
      map.set(p.id, {
        id: p.id,
        projectKey: p.projectKey,
        name: p.name,
        parentProjectId: p.parentProjectId ?? null,
        icon: p.icon ?? null,
        iconType: p.iconType ?? null,
        iconColor: p.iconColor ?? null,
        isCyclesEnabled: p.isCyclesEnabled,
      })
    }
    return map
  }, [projects])

  const statusMap = useMemo(
    () => new Map(statuses.map((status) => [status.id, status])),
    [statuses],
  )

  /** The space a project sits in (a space is its own). */
  const spaceOf = useCallback(
    (projectId: string | null | undefined) => {
      if (!projectId) return null
      const project = projectMap.get(projectId)
      if (!project) return null
      return project.parentProjectId ? (projectMap.get(project.parentProjectId) ?? null) : project
    },
    [projectMap],
  )

  // Where you are: a space route scopes to that space; a project route
  // scopes to its space and lets a bare number jump to its task.
  const { routeSpace, routeProjectId } = useMemo(() => {
    const byKey = (key: string) => {
      const lower = key.toLowerCase()
      for (const project of projectMap.values()) {
        if (project.projectKey.toLowerCase() === lower) return project
      }
      return null
    }
    const projectMatch = pathname.match(/\/projects\/([^/]+)/)
    const project = projectMatch?.[1] ? byKey(decodeURIComponent(projectMatch[1])) : null
    const spaceMatch = pathname.match(/\/spaces\/([^/]+)/)
    const space = spaceMatch?.[1] ? byKey(decodeURIComponent(spaceMatch[1])) : null
    return {
      routeSpace: space ?? (project ? spaceOf(project.id) : null),
      routeProjectId: project?.id ?? null,
    }
  }, [pathname, projectMap, spaceOf])

  const scope = useMemo<SearchScope | null>(() => {
    if (scopeOverride !== undefined) return scopeOverride
    if (!routeSpace) return null
    return {
      kind: 'space',
      id: routeSpace.id,
      name: routeSpace.name,
      icon: routeSpace.icon,
      iconType: routeSpace.iconType,
      iconColor: routeSpace.iconColor,
    }
  }, [scopeOverride, routeSpace])

  const searchItems = useMemo<Array<SearchItem>>(() => {
    const items: Array<SearchItem> = []

    for (const t of tasks) {
      if (t.isDeleted || t.isArchived) continue
      const project = t.projectId ? projectMap.get(t.projectId) : undefined
      const space = spaceOf(t.projectId)
      const status = t.statusId ? statusMap.get(t.statusId) : undefined
      items.push({
        id: t.id,
        name: t.name,
        type: 'task',
        projectId: t.projectId ?? undefined,
        projectKey: project?.projectKey,
        projectName: project?.name,
        identifier: resolveTaskIdentifier(t, project?.projectKey) || undefined,
        seqNo: t.seqNo ?? null,
        statusId: t.statusId,
        progressType: status?.progressType,
        spaceId: space?.id ?? null,
        spaceName: space?.name,
        updatedOn: t.updatedOn,
      })
    }

    for (const p of projectMap.values()) {
      const space = spaceOf(p.id)
      items.push({
        id: p.id,
        name: p.name,
        type: 'project',
        projectKey: p.projectKey,
        icon: p.icon,
        iconType: p.iconType,
        iconColor: p.iconColor,
        parentProjectId: p.parentProjectId,
        spaceId: space?.id ?? null,
        spaceName: space?.name,
      })
    }

    for (const p of pages) {
      if (p.isDeleted) continue
      const project = p.projectId ? projectMap.get(p.projectId) : undefined
      const space = spaceOf(p.projectId)
      items.push({
        id: p.id,
        name: p.title,
        type: 'page',
        slug: p.slug,
        projectId: p.projectId,
        projectKey: project?.projectKey,
        projectName: project?.name,
        icon: p.icon,
        iconType: p.iconType,
        iconColor: p.iconColor,
        spaceId: space?.id ?? null,
        spaceName: space?.name,
        updatedOn: p.updatedOn,
      })
    }

    for (const n of notes) {
      if (n.isDeleted) continue
      items.push({
        id: n.id,
        name: n.title,
        type: 'note',
        noteType: n.noteType as 'daily' | 'weekly',
        noteDate: n.noteDate,
        spaceId: null,
        updatedOn: n.updatedOn,
      })
    }

    return items
  }, [tasks, projectMap, pages, notes, statusMap, spaceOf])

  const itemById = useMemo(
    () => new Map(searchItems.map((item) => [`${item.type}:${item.id}`, item])),
    [searchItems],
  )

  // Fuzzy matching only rescues typos; contiguous matches rank first.
  const fuse = useMemo(
    () =>
      new Fuse(searchItems, {
        keys: ['name'],
        threshold: 0.2,
        includeScore: true,
        ignoreLocation: true,
        minMatchCharLength: 2,
      }),
    [searchItems],
  )

  const ranked = useMemo(() => {
    const q = debouncedQuery.trim()
    if (!q) return []
    const fuzzy = new Map<string, number>()
    // Typo rescue needs a word long enough that one slip isn't a different word.
    if (q.length >= 5) {
      for (const hit of fuse.search(q, { limit: 100 })) {
        fuzzy.set(`${hit.item.type}:${hit.item.id}`, hit.score ?? 1)
      }
    }
    return rankSearch(searchItems, q, { scope, routeProjectId, fuzzy })
  }, [debouncedQuery, fuse, searchItems, scope, routeProjectId])

  const counts = useMemo(() => countByType(ranked), [ranked])
  const groups = useMemo(
    () =>
      groupResults(
        ranked,
        activeFilter,
        activeFilter === 'all' ? ALL_GROUP_CAP : FILTERED_CAP,
      ),
    [ranked, activeFilter],
  )

  // Recently opened, refreshed from live data so renames show.
  const recentItems = useMemo(() => {
    if (!workspaceId) return []
    return getRecentItems(workspaceId)
      .map((item) => itemById.get(`${item.type}:${item.id}`) ?? null)
      .filter((item): item is SearchItem => item !== null)
    // re-read when the query changes (the dialog re-opens with an empty query)
  }, [workspaceId, itemById, query])

  const addRecentItem = useCallback(
    (item: SearchItem) => {
      if (!workspaceId) return
      const existing = getRecentItems(workspaceId)
      const filtered = existing.filter((r) => !(r.id === item.id && r.type === item.type))
      const updated = [item, ...filtered].slice(0, MAX_RECENT_ITEMS)
      saveRecentItems(workspaceId, updated)
    },
    [workspaceId],
  )

  const reset = useCallback(() => {
    setQuery('')
    setActiveFilter('all')
    setScopeOverride(undefined)
  }, [])

  return {
    query,
    setQuery,
    /** True once the debounced query has caught up with what's typed. */
    isSettled: debouncedQuery === query,
    activeFilter,
    setActiveFilter,
    scope,
    setScope: setScopeOverride,
    routeProjectId,
    groups,
    counts,
    recentItems,
    addRecentItem,
    reset,
    isLoading,
    projectMap,
    statusMap,
    tasks,
  }
}
